import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { Heart, Users, Award, MapPin, ArrowRight, Quote } from 'lucide-react';

const teamValues = [
  {
    icon: Heart,
    title: 'Compassion',
    description: 'Every caregiver embodies genuine empathy and kindness.',
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'Committed to the highest standards of care and professionalism.',
  },
  {
    icon: Users,
    title: 'Family',
    description: 'We treat every client like a member of our own family.',
  },
];

const timeline = [
  { year: '2009', event: 'Heritage Home Health LLC founded with a mission to transform in-home care' },
  { year: '2012', event: 'Expanded services to include specialized memory care support' },
  { year: '2015', event: 'Reached milestone of serving 1,000 families' },
  { year: '2018', event: 'Launched comprehensive caregiver training program' },
  { year: '2021', event: 'Recognized as a top-rated home care provider in the region' },
  { year: '2024', event: 'Celebrating 15 years of compassionate care and 5,000+ families served' },
];

export const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Heritage Home Health LLC | Our Story & Mission</title>
        <meta
          name="description"
          content="Learn about Heritage Home Health LLC's mission, values, and 15+ year journey providing compassionate in-home care services to families in our community."
        />
      </Helmet>
      <div className="min-h-screen bg-warm-beige" data-testid="about-page">
        <Navigation />
        <main>
          {/* Hero Section */}
          <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-soft-gold/10 via-transparent to-transparent" />
            <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-gradient-to-br from-muted-green/10 to-transparent rounded-full blur-3xl" />
            
            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                    Our Story
                  </span>
                  <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy mb-6">
                    About{' '}
                    <span className="text-gradient-gold">Heritage Home Health LLC</span>
                  </h1>
                  <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-6">
                    Heritage Home Health LLC was founded on a simple but powerful belief: 
                    that everyone deserves to receive compassionate, dignified care in the 
                    comfort of their own home.
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    What began as a small, family-operated care service has grown into a 
                    trusted provider serving thousands of families throughout our community, 
                    while never losing sight of the personal touch that defines who we are.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative"
                >
                  <div className="rounded-3xl overflow-hidden shadow-soft-3d-lg">
                    <img
                      src="https://images.unsplash.com/photo-1758686253677-d3af6c15186e?crop=entropy&cs=srgb&fm=jpg&w=600&h=500&fit=crop"
                      alt="Happy senior couple"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="absolute -bottom-8 -left-8 glass shadow-soft-3d rounded-2xl p-6 max-w-xs animate-float"
                  >
                    <Quote className="w-8 h-8 text-soft-gold mb-3" />
                    <p className="text-navy font-medium italic">
                      "Care that honors the whole person—their history, their preferences, their dignity."
                    </p>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Mission Section */}
          <section className="py-16 md:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="order-2 lg:order-1"
                >
                  <div className="relative rounded-3xl overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1613394242132-1268854bde44?crop=entropy&cs=srgb&fm=jpg&w=600&h=450&fit=crop"
                      alt="Compassionate caregiver"
                      className="w-full h-auto object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="order-1 lg:order-2"
                >
                  <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                    Our Mission
                  </span>
                  <h2 className="font-serif text-3xl md:text-4xl text-navy mb-6">
                    Empowering Independence, <span className="text-gradient-gold">Enriching Lives</span>
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed mb-6">
                    Our mission at Heritage Home Health LLC is to enhance the quality of life 
                    for individuals and families by providing exceptional, personalized in-home 
                    care that promotes independence, dignity, and well-being.
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    We are committed to treating every person we serve with the respect, 
                    compassion, and attention they deserve—because we understand that 
                    choosing care for a loved one is one of life's most important decisions.
                  </p>

                  <div className="grid grid-cols-3 gap-6">
                    {teamValues.map((value, index) => {
                      const Icon = value.icon;
                      return (
                        <motion.div
                          key={value.title}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className="text-center"
                        >
                          <div className="w-12 h-12 rounded-xl bg-soft-gold/20 flex items-center justify-center mx-auto mb-3">
                            <Icon className="w-6 h-6 text-soft-gold" />
                          </div>
                          <h4 className="font-semibold text-navy text-sm">{value.title}</h4>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Timeline Section */}
          <section className="py-16 md:py-24 bg-warm-beige">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                  Our Journey
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-navy mb-6">
                  15+ Years of <span className="text-gradient-gold">Trusted Care</span>
                </h2>
              </motion.div>

              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-soft-gold/30 -translate-x-1/2" />

                <div className="space-y-12">
                  {timeline.map((item, index) => (
                    <motion.div
                      key={item.year}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                    >
                      {/* Dot */}
                      <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-soft-gold rounded-full -translate-x-1/2 shadow-glow-gold" />
                      
                      {/* Content */}
                      <div className={`ml-16 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-16 md:text-right' : 'md:pl-16'}`}>
                        <div className="glass-strong shadow-soft-3d rounded-2xl p-6 inline-block">
                          <span className="text-soft-gold font-bold text-lg">{item.year}</span>
                          <p className="text-navy mt-2">{item.event}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Location Section */}
          <section className="py-16 md:py-24 bg-white">
            <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 rounded-2xl bg-soft-gold/20 flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-8 h-8 text-soft-gold" />
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-navy mb-6">
                  Proudly Serving Our Community
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  Heritage Home Health LLC is proud to serve families throughout our region. 
                  Our local roots and community connections allow us to provide truly 
                  personalized care that understands and respects local values and needs.
                </p>
                <Link
                  to="/contact"
                  data-testid="about-page-cta"
                  className="group inline-flex items-center space-x-2 bg-navy text-white px-8 py-4 rounded-full font-semibold hover:bg-deep-blue transition-all shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1"
                >
                  <span>Contact Heritage Home Health LLC</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </section>

          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AboutPage;
